'use client';

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { Star, Share2, Heart, User } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { Amenity, Rating, Voucher } from 'hotdesk_typescript';
import { MyListing } from '@/hotdesk-ui/services/listing.service';
import { LanguageCode } from '@/hotdesk-ui/constants/langulage.constant';
import { Status } from '@/hotdesk-ui/enum/status-keys';
import { useAppDispatch, useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { addFavorite, removeFavorite } from '@/hotdesk-ui/store/slices/favorites.slice';
import { showFavoriteToast } from '@/hotdesk-ui/helpers/favorite-toast';
import { firebaseAnalytics } from '@/hotdesk-ui/common-classes/firebase.service';
import { AuthPopupService } from '@/hotdesk-ui/services/authPopup.service';
import ErroModalClient from '@/hotdesk-ui/components/error-modal/ErrorModalClient';
import CarouselSection from '@/hotdesk-ui/components/shared/CarouselSection';
import InactiveListingView from './InactiveListingView';
import LocationBreadcrumb from './LocationBreadcrumb';
const ShareModal = dynamic(() => import('./ShareModal'), {
  ssr: false
});
const BusinessForm = dynamic(() => import('@/hotdesk-ui/components/businessForm/BusinessFormClient'), {
  ssr: false
});
const RequestVisitModal = dynamic(() => import('@/hotdesk-ui/components/request-visit-modal/RequestVisitModalClient'), {
  ssr: false
});
import { tracking } from '@/hotdesk-ui/services/tracking.service';
import { getProviderTrackingProps } from '@/hotdesk-ui/utils/provider';
import ImageGallery from './ImageGallery';
import ProviderLabel from '@/hotdesk-ui/components/booking-v4/ProviderLabel';
import StickyNav, { StickyNavTab } from './StickyNav';
import AboutSection from './AboutSection';
import AmenitiesGrid from './AmenitiesGrid';
import ReviewsSection from './ReviewsSection';
import LocationSection from './LocationSection';
import PromoBanner from './PromoBanner';
import MembershipOfferSection from './MembershipOfferSection';
import BookingSidebar from './BookingSidebar';
import MobileStickyBar from './MobileStickyBar';
import { parseRatingFromMetaData } from '@/hotdesk-ui/helpers/rating.utils';
import { buildWorkspacePath } from '@/hotdesk-ui/helpers/workspace-url';
import { useWebReviewsDisplay } from '@/hotdesk-ui/hooks/useWebReviewsDisplay';
import SubscriptionPlanBanner from './SubscriptionPlanBanner';
import styles from './ListingDetailsClient.module.scss';
import ContactUs from '@/hotdesk-ui/components/details-v4/ContactUsOptions';
import WorkspaceListings from '@/hotdesk-ui/components/popup/workspace-listing/WorkspaceListings';
import { getSubscriptionPromoDetails, hasUserApplicableSubscriptions, resolveInitialPriceUnit, resolveSubscriptionForUnit } from '@/hotdesk-ui/utils/listingSubscriptionUtils';
import { usePreferredUserSubscriptionIds } from '@/hotdesk-ui/hooks/usePreferredUserSubscriptionIds';
import { useListingApplicableSubscriptions, useSubscriberSimilarListings } from '@/hotdesk-ui/hooks/useListingMemberData';
import { getActiveUserSubscriptions } from '@/hotdesk-ui/utils/subscriptionUtils';
import CompareThisButton from '@/hotdesk-ui/components/compare/details/CompareThisButton';
import { CompareType } from '@/hotdesk-ui/enum/compare';
export interface ListingDetailsClientProps {
  overAllRating: number;
  ratingCount: number;
  ratingCriteria: any[];
  ratingCriteriaAverage: any[];
  listingDetails: any;
  languageCode: LanguageCode;
  bestPrice: boolean;
  coupons: Voucher[] | undefined;
  amenities: Amenity[];
  reviews: Rating[];
  /** API total of published reviews; `reviews` is only the first page. */
  reviewsTotal?: number;
  isError: boolean;
  rating_value: number;
  similarListings: MyListing[];
  otherWorkspaceListings: MyListing[];
  workspaceListingCount: number;
}
const ListingDetailsClient: FC<ListingDetailsClientProps> = ({
  listingDetails,
  languageCode,
  overAllRating,
  ratingCount,
  coupons,
  amenities,
  reviews,
  reviewsTotal,
  isError,
  similarListings: baseSimilarListings,
  otherWorkspaceListings,
  workspaceListingCount
}) => {
  const t = useTranslations('workspace-details');
  const tFavorites = useTranslations('favorites');
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state: RootState) => state.auth.user);
  const favoritesMap = useAppSelector((state: RootState) => state.favorites.items);
  const amenitiesMap = useAppSelector((state: RootState) => state.amenities.items);
  const preferredIds = usePreferredUserSubscriptionIds();
  const fallbackUserSubs = useMemo(() => getActiveUserSubscriptions(user) as unknown as Record<string, unknown>[], [user]);

  // Member-only data is fetched client-side — never rendered on the server
  // (see useListingMemberData for the edge-cache leak this avoids).
  const ApplicableSubscriptions = useListingApplicableSubscriptions(listingDetails?.id, languageCode);
  const similarListingsFilters = useMemo(() => listingDetails?.workspace?.location?.coordinates ? {
    loc: [listingDetails.workspace.location.coordinates[1], listingDetails.workspace.location.coordinates[0], 15].toString(),
    type: listingDetails?.type,
    status: '1',
    workspaceStatus: 1,
    limit: 12
  } : null, [listingDetails]);
  const similarListings = useSubscriberSimilarListings(baseSimilarListings, similarListingsFilters, listingDetails?.id, 'listing', languageCode);
  const [subscription, setSubscription] = useState<string | null>(null);
  const defaultPriceUnit = useMemo(() => {
    const {
      appliesFor
    } = getSubscriptionPromoDetails(ApplicableSubscriptions, listingDetails?.type, preferredIds, fallbackUserSubs);
    return resolveInitialPriceUnit({
      priceObj: listingDetails?.rules?.price,
      frequencyParam: searchParams.get('frequency'),
      appliesFor
    });
  }, [ApplicableSubscriptions, listingDetails?.type, listingDetails?.id, listingDetails?.rules?.price, searchParams, preferredIds, fallbackUserSubs]);
  const [price_unit, setPrice_unit] = useState(defaultPriceUnit);
  useEffect(() => {
    setPrice_unit(defaultPriceUnit);
  }, [defaultPriceUnit]);

  // Legacy ListingLabels: subscription query param is package name when unit is in appliesFor.
  useEffect(() => {
    setSubscription(resolveSubscriptionForUnit(ApplicableSubscriptions, listingDetails?.type, price_unit, listingDetails, preferredIds, fallbackUserSubs));
  }, [ApplicableSubscriptions, listingDetails, price_unit, preferredIds, fallbackUserSubs]);
  const suppressCouponsForSubscription = hasUserApplicableSubscriptions(ApplicableSubscriptions, listingDetails);
  const galleryRef = useRef<HTMLDivElement>(null);
  const amenitiesRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const showReviews = useWebReviewsDisplay('listing_details');
  const [activeTab, setActiveTab] = useState('amenities');
  const [shareOpen, setShareOpen] = useState(false);
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [requestVisitOpen, setRequestVisitOpen] = useState(false);
  const [contactUsBoxOpen, setcontactUsBoxOpen] = useState(false);
  const [workspaceListingsPopup, setWorkspaceListingsPopup] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // Favorite state
  const listingId = listingDetails?.id as number | undefined;
  const favoriteId = listingId ? favoritesMap[listingId] ?? null : null;
  const isFavorite = favoriteId !== null;
  const tabs: StickyNavTab[] = [{
    id: 'amenities',
    label: t('amenities')
  }, {
    id: 'location',
    label: t('location_title')
  }, ...(showReviews && reviews.length > 0 ? [{
    id: 'reviews',
    label: t('reviews')
  }] : [])];
  const sectionRefs: Record<string, React.RefObject<HTMLDivElement | null>> = {
    amenities: amenitiesRef,
    location: locationRef,
    reviews: reviewsRef
  };
  const handleTabClick = (id: string) => {
    const ref = sectionRefs[id];
    if (ref?.current) {
      const top = ref.current.getBoundingClientRect().top + window.scrollY - 200;
      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    }
  };

  // Track active tab based on scroll position
  useEffect(() => {
    const entries = Object.entries(sectionRefs);
    const observer = new IntersectionObserver(observed => {
      for (const entry of observed) {
        if (entry.isIntersecting) {
          const id = entries.find(([, ref]) => ref.current === entry.target)?.[0];
          if (id) setActiveTab(id);
        }
      }
    }, {
      rootMargin: '-120px 0px -60% 0px',
      threshold: 0
    });
    entries.forEach(([, ref]) => {
      if (ref.current) observer.observe(ref.current);
    });
    return () => observer.disconnect();
  }, [reviews.length]);
  const handleShare = () => {
    setShareOpen(true);
  };
  const handleFavorite = async () => {
    if (!user) {
      new AuthPopupService().getPopup();
      return;
    }
    if (!listingId) return;
    try {
      if (isFavorite && favoriteId) {
        await dispatch(removeFavorite({
          listingId,
          favoriteId
        })).unwrap();
        firebaseAnalytics('listing_unfavorited', {
          listingId
        });
        showFavoriteToast({
          message: tFavorites('removedToast'),
          viewAll: tFavorites('viewAll')
        });
      } else {
        await dispatch(addFavorite({
          userId: user.id!,
          listingId
        })).unwrap();
        firebaseAnalytics('listing_favorited', {
          listingId
        });
        showFavoriteToast({
          message: tFavorites('addedToast'),
          viewAll: tFavorites('viewAll')
        });
      }
    } catch (error) {
      console.error('Error updating favorite:', error);
    }
  };
  const handleContactSales = () => {
    setContactFormOpen(true);
  };
  const handleRequestViewing = () => {
    setRequestVisitOpen(true);
  };
  const handleContactUs = () => {
    setcontactUsBoxOpen(true);
  };
  const manageContactSales = () => {
    setcontactUsBoxOpen(false);
    setContactFormOpen(true);
  };

  // Tracking (preserved from ListingClientWrapper)
  useEffect(() => {
    if (!listingDetails?.id || typeof window === 'undefined') return;
    const priceKeys = Object.keys(listingDetails?.rules?.price || {});
    const unit = priceKeys[0] ?? '';
    const price = listingDetails?.rules?.price?.[unit] ?? 0;
    tracking.onListingViewed(listingDetails.id, listingDetails?.workspace?.id ?? '', listingDetails?.rules?.currency ?? '', listingDetails?.title ?? '', listingDetails?.typeName ?? String(listingDetails?.type ?? ''), price, listingDetails?.workspace?.title ?? '', amenities.map(a => a.name).filter(Boolean) as string[], window.location.href, getProviderTrackingProps(listingDetails));
  }, [listingDetails?.id]);

  // Derived values
  const listingName = listingDetails?.title || '';
  const workspaceName = listingDetails?.workspace?.title || '';
  const locationName = listingDetails?.city?.name || listingDetails?.workspace?.metaData?.detailedLocation?.area || '';
  const locationNameinTitle = '';
  const locationContext = locationNameinTitle ? `, ${locationNameinTitle}` : '';
  const listingH1 = `${listingName} at ${workspaceName}${locationContext}`;
  const listingRating = parseRatingFromMetaData(listingDetails?.metaData);
  const effectiveOverallRating = listingRating.overall;
  const effectiveRatingCount = listingRating.count;
  const rawRating = listingRating.displayRating;
  const capacity = (listingDetails as any)?.capacity || listingDetails?.metaData?.capacity || 1;
  const workspaceLocation = [listingDetails?.workspace?.metaData?.detailedLocation?.area, listingDetails?.city?.name].filter(Boolean).join(' - ');
  const amenityIds = listingDetails.amenities ? (Array.isArray(listingDetails.amenities) ? listingDetails.amenities : Array.from(listingDetails.amenities as Iterable<number>)) as number[] : [];
  const resolvedAmenities = amenityIds.map(id => amenitiesMap[id]).filter(Boolean);
  const title = `${listingDetails?.workspace?.title} ${listingDetails?.title} in ${listingDetails?.city?.name} showing ${!!resolvedAmenities[0] ? resolvedAmenities[0]?.name : ''} ${!!resolvedAmenities[1] ? `, ${resolvedAmenities[1]?.name}` : ''}`;
  const apiDescription = listingDetails?.descriptions?.[languageCode] || listingDetails?.rules?.description || '';
  const knownCapacity = listingDetails?.rules?.capacity || listingDetails?.metaData?.capacity;
  const fallbackAmenityNames = amenities.map(a => a.name).filter(Boolean).slice(0, 5);
  // Keeps listings whose API description is empty from rendering with no unique text.
  const aboutDescription = apiDescription || [workspaceName && locationName ? t('aboutFallback', {
    type: listingDetails?.listingTypeName || listingName,
    workspace: workspaceName,
    city: locationName
  }) : '', fallbackAmenityNames.length ? t('aboutFallbackAmenities', {
    amenities: fallbackAmenityNames.join(', ')
  }) : '', Number(knownCapacity) > 0 ? t('aboutFallbackCapacity', {
    capacity: Number(knownCapacity)
  }) : ''].filter(Boolean).join(' ');
  const workspaceUrl = `/${buildWorkspacePath({
    country: listingDetails?.workspace?.workspaceCity?.country,
    city: listingDetails?.workspace?.workspaceCity || listingDetails?.city,
    workspaceSlug: listingDetails?.workspace?.slug
  })}`;
  if (!listingDetails || !languageCode) return null;

  // Show inactive listing view if listing is not active
  if (listingDetails.status === Status.Inactive) {
    return <InactiveListingView listing={listingDetails} similarListings={similarListings} otherWorkspaceListings={otherWorkspaceListings} workspaceListingCount={workspaceListingCount} languageCode={languageCode} />;
  }
  return <div className={styles.page} data-sentry-component="ListingDetailsClient" data-sentry-source-file="ListingDetailsClient.tsx">
            {/* Image Gallery */}
            <div ref={galleryRef} className={styles.galleryWrap}>
                <ImageGallery images={listingDetails?.rules?.images || {}} title={title || ''} data-sentry-element="ImageGallery" data-sentry-source-file="ListingDetailsClient.tsx" />
            </div>

            {/* Two-column container: left = all content, right = sticky sidebar */}
            <div className={styles.container}>
                {/* Left column */}
                <div className={styles.mainCol}>
                    {/* Header: Title, Location, Stats */}
                    <div className={styles.headerSection}>
                        <div className={styles.titleRow}>
                            <h1 className={styles.title}>{listingH1}</h1>
                            <div className={styles.actions}>
                                <button type="button" className={styles.actionBtn} aria-label="Share" onClick={handleShare}>
                                    <Share2 size={20} data-sentry-element="Share2" data-sentry-source-file="ListingDetailsClient.tsx" />
                                </button>
                                <button type="button" className={`${styles.actionBtn} ${isFavorite ? styles.actionBtnActive : ''}`} aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'} onClick={handleFavorite}>
                                    <Heart size={20} fill={isFavorite ? '#000' : 'none'} color={isFavorite ? '#000' : 'currentColor'} data-sentry-element="Heart" data-sentry-source-file="ListingDetailsClient.tsx" />
                                </button>
                            </div>
                        </div>

                        <LocationBreadcrumb city={listingDetails.city} data-sentry-element="LocationBreadcrumb" data-sentry-source-file="ListingDetailsClient.tsx" />

                        <div className={styles.locationRow}>
                            <span className={styles.locationDot} />
                            <span className={styles.locationText}>
                                <Link href={workspaceUrl} className={styles.body} target="_blank" data-sentry-element="Link" data-sentry-source-file="ListingDetailsClient.tsx">
                                   In {workspaceName} - {workspaceLocation}
                                </Link> 
                            </span>
                            <ProviderLabel listing={listingDetails} variant="subtle" data-sentry-element="ProviderLabel" data-sentry-source-file="ListingDetailsClient.tsx" />
                        </div>

                        <div className={styles.statsRow}>
                            {showReviews && rawRating ? <span className={styles.statItem}>
                                    <Star size={14} fill="#000" color="#000" />
                                    {rawRating}
                                    {effectiveRatingCount > 0 && ` (${effectiveRatingCount})`}
                                </span> : showReviews ? <span className={styles.statItemMuted}>{t('noRatingYet')}</span> : null}
                            
                            <span className={styles.statItem}>
                                <User size={14} data-sentry-element="User" data-sentry-source-file="ListingDetailsClient.tsx" /> {capacity} {capacity === 1 ? t('guest') : t('guests')}
                            </span>
                            <CompareThisButton type={CompareType.LISTING} id={listingDetails?.id} name={listingDetails?.title ?? ''} data-sentry-element="CompareThisButton" data-sentry-source-file="ListingDetailsClient.tsx" />
                        </div>
                    </div>

                    {/* Sticky Nav — scroll to section */}
                    <StickyNav tabs={tabs} activeTab={activeTab} onTabClick={handleTabClick} data-sentry-element="StickyNav" data-sentry-source-file="ListingDetailsClient.tsx" />

                    {/* Promo + About */}
                    <PromoBanner coupons={coupons} suppressCoupons={suppressCouponsForSubscription} showCustomQuote={true} onCustomQuoteClick={handleRequestViewing} membershipOffer={<MembershipOfferSection listingDetails={listingDetails} languageCode={languageCode} hasActiveSubscription={suppressCouponsForSubscription} />} data-sentry-element="PromoBanner" data-sentry-source-file="ListingDetailsClient.tsx" />
                    <SubscriptionPlanBanner applicableSubscriptions={ApplicableSubscriptions} listing={listingDetails} priceUnit={price_unit} subscription={subscription} data-sentry-element="SubscriptionPlanBanner" data-sentry-source-file="ListingDetailsClient.tsx" />
                    <AboutSection description={aboutDescription} data-sentry-element="AboutSection" data-sentry-source-file="ListingDetailsClient.tsx" />

                    {/* Amenities section */}
                    <div ref={amenitiesRef} id="amenities">
                        <AmenitiesGrid amenities={amenities} data-sentry-element="AmenitiesGrid" data-sentry-source-file="ListingDetailsClient.tsx" />
                    </div>

                    {/* Location section */}
                    <div ref={locationRef} id="location">
                        <LocationSection listing={listingDetails} cardItem={listingDetails} cardType="listing" cardUrl={`/${buildWorkspacePath({
            country: listingDetails?.city?.country,
            city: listingDetails?.city,
            workspaceSlug: listingDetails?.workspace?.slug,
            listingSlug: listingDetails?.slug
          })}`} data-sentry-element="LocationSection" data-sentry-source-file="ListingDetailsClient.tsx" />
                    </div>
                </div>

                {/* Right column — sticky sidebar */}
                <aside className={styles.sideCol}>
                    <BookingSidebar variant="listing" listing={listingDetails} applicableSubscriptions={ApplicableSubscriptions} subscription={subscription} price_unit={price_unit} setPrice_unit={setPrice_unit} onContactSales={handleContactSales} data-sentry-element="BookingSidebar" data-sentry-source-file="ListingDetailsClient.tsx" />
                </aside>
            </div>

            {/* Full-width sections below the two-col container */}
            {otherWorkspaceListings.length > 0 && <div className={styles.fullWidthSection}>
                    <CarouselSection title={t('otherListingsInWorkspace')} listings={otherWorkspaceListings as any} variant="listing" showBookNow workspaceListingCount={workspaceListingCount} onWorkspacesClick={() => setWorkspaceListingsPopup({
        id: listingDetails?.workspace?.id || 0,
        name: listingDetails?.workspace?.title || ''
      })} />
                </div>}

            {showReviews && reviews.length > 0 && <div ref={reviewsRef} id="reviews" className={styles.fullWidthSection}>
                    <ReviewsSection reviews={reviews} reviewsTotal={reviewsTotal} overAllRating={effectiveOverallRating} ratingCount={effectiveRatingCount} trackingSource="listing_details" listingId={listingDetails?.id} workspaceId={listingDetails?.workspace?.id} />
                </div>}

            {similarListings.length > 0 && <div className={styles.fullWidthSection}>
                    <CarouselSection title={t('similarListings')} listings={similarListings as any} variant="listing" showBookNow />
                </div>}

            {workspaceListingsPopup && <WorkspaceListings showPopup={!!workspaceListingsPopup} workspaceId={workspaceListingsPopup.id} workspaceName={workspaceListingsPopup.name} languageCode={languageCode} onClose={() => setWorkspaceListingsPopup(null)} showBookNow />}

            {/* Mobile Sticky Bar */}
            <MobileStickyBar variant="listing" visible listing={listingDetails} applicableSubscriptions={ApplicableSubscriptions} subscription={subscription} price_unit={price_unit} setPrice_unit={setPrice_unit} onContactUs={handleContactUs} data-sentry-element="MobileStickyBar" data-sentry-source-file="ListingDetailsClient.tsx" />

            {/* Share Modal */}
            <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} variant="listing" item={listingDetails} workspaceTitle={listingDetails?.workspace?.title} data-sentry-element="ShareModal" data-sentry-source-file="ListingDetailsClient.tsx" />

            {/* Contact Form Modal */}
            {contactFormOpen && <div className={styles.modalOverlay} onClick={() => setContactFormOpen(false)}>
                    <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <BusinessForm onClose={() => setContactFormOpen(false)} workspaceId={listingDetails?.workspace?.id} listingId={listingDetails?.id} />
                    </div>
                </div>}
            <RequestVisitModal open={requestVisitOpen} onClose={() => setRequestVisitOpen(false)} languageCode={languageCode} listingId={listingDetails?.id} listingName={listingDetails?.title ?? ''} workspaceId={listingDetails?.workspace?.id} workspaceName={listingDetails?.workspace?.title ?? ''} source="listing_details" data-sentry-element="RequestVisitModal" data-sentry-source-file="ListingDetailsClient.tsx" />
            {contactUsBoxOpen && <div className={styles.modalOverlay} onClick={() => setcontactUsBoxOpen(false)}>
                    <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <ContactUs listing={listingDetails} manageContactSales={manageContactSales} onClose={() => setcontactUsBoxOpen(false)} />
                    </div>
                </div>}

            <ErroModalClient show={isError} data-sentry-element="ErroModalClient" data-sentry-source-file="ListingDetailsClient.tsx" />
        </div>;
};
export default ListingDetailsClient;